import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Circle, 
  CheckCircle2, 
  Loader2,
  ChevronDown,
  ChevronRight,
  Clock
} from 'lucide-react';

export const QueryPlan = ({ plan, onComplete, onStepChange, forceCollapse = false, onExpandChange }) => {
  const { thinking, todos } = plan;
  const [isExpanded, setIsExpanded] = useState(true);
  const [visibleTodos, setVisibleTodos] = useState(0);
  const [todoStatuses, setTodoStatuses] = useState(todos.map(() => 'pending'));
  const [showThinking, setShowThinking] = useState(false);
  
  const animationStarted = useRef(false);
  const completionTriggered = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const onStepChangeRef = useRef(onStepChange);
  const onExpandChangeRef = useRef(onExpandChange);
  
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onStepChangeRef.current = onStepChange;
    onExpandChangeRef.current = onExpandChange;
  }, [onComplete, onStepChange, onExpandChange]);

  // Auto-collapse when forceCollapse is true
  useEffect(() => {
    if (forceCollapse) {
      setIsExpanded(false);
    }
  }, [forceCollapse]);

  // Notify parent of expand state changes
  useEffect(() => {
    if (onExpandChangeRef.current) {
      onExpandChangeRef.current(isExpanded);
    }
  }, [isExpanded]);

  useEffect(() => {
    if (animationStarted.current) return;
    animationStarted.current = true;

    // Show thinking
    setTimeout(() => {
      setShowThinking(true);
      if (onStepChangeRef.current) onStepChangeRef.current();
    }, 500);

    // Animate todos one by one
    todos.forEach((_, index) => {
      // Show todo as in_progress
      setTimeout(() => {
        setVisibleTodos(index + 1);
        setTodoStatuses(prev => {
          const updated = [...prev];
          updated[index] = 'in_progress';
          return updated;
        });
        if (onStepChangeRef.current) onStepChangeRef.current();
      }, 1200 + (index * 1800));
      
      // Mark as complete
      setTimeout(() => {
        setTodoStatuses(prev => {
          const updated = [...prev];
          updated[index] = 'completed';
          return updated;
        });
        
        // Trigger completion after last todo
        if (index === todos.length - 1 && !completionTriggered.current) {
          completionTriggered.current = true;
          setTimeout(() => {
            if (onCompleteRef.current) onCompleteRef.current();
          }, 600);
        }
      }, 1200 + (index * 1800) + 1200);
    });
  }, []);

  const allComplete = todoStatuses.every(s => s === 'completed');

  return (
    <div className="mb-3" data-testid="query-plan">
      <div className="rounded-xl bg-slate-950/50 overflow-hidden">
        {/* Header - Clickable */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full text-left p-4 flex items-center justify-between hover:bg-slate-900/30 transition-colors"
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-gray-500 shrink-0">
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </span>
            {!isExpanded && (
              <p className="text-xs text-gray-400 truncate">
                {thinking ? `"${thinking.length > 60 ? thinking.substring(0, 60) + '...' : thinking}"` : 'Analyzing request and designing execution strategy'}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {allComplete ? (
              <>
                <span className="flex items-center gap-1 text-[10px] text-gray-500">
                  <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  {todos.length} steps
                </span>
                <span className="flex items-center gap-1 text-[10px] text-gray-500 font-mono">
                  <Clock className="w-3 h-3" />1.2s
                </span>
              </>
            ) : (
              <span className="flex items-center gap-1.5 text-xs text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full">
                <Loader2 className="w-3 h-3 animate-spin" />
                Planning
              </span>
            )}
          </div>
        </button>

        {/* Content */}
        {isExpanded && (
          <div className="px-4 pb-4 -mt-1">
            {/* Understanding */}
            {thinking && showThinking && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mb-3 pb-3 border-b border-slate-800/50"
              >
                <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Understanding</span>
                <p className="text-xs text-gray-400 leading-relaxed mt-1">{thinking}</p>
              </motion.div>
            )}

            {/* Execution Plan */}
            <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Execution Plan</span>
            <div className="space-y-2 mt-1">
              {todos.slice(0, visibleTodos).map((todo, index) => {
                const status = todoStatuses[index];
                return (
                  <motion.div
                    key={todo.id}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-start gap-2"
                  >
                    {status === 'completed' ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    ) : status === 'in_progress' ? (
                      <Loader2 className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5 animate-spin" />
                    ) : (
                      <Circle className="w-3.5 h-3.5 text-gray-600 flex-shrink-0 mt-0.5" />
                    )}
                    <span className="text-[10px] font-mono text-gray-600 flex-shrink-0 mt-px">{index + 1}.</span>
                    <span className={`text-xs leading-relaxed -ml-1 ${status === 'completed' ? 'text-gray-300' : 'text-gray-500'}`}>
                      {todo.text}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QueryPlan;
